import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject,
    SoundType,
} from '@denishussar/html5-game-engine';
import { SpineAnimationBaseController } from '../../Spine/SpineAnimationBaseController';
import { Graphics, Sprite } from 'pixi.js';
import { AnimationContents } from '../../../../AnimationContents';
import { PropsLogic } from '../../../Logic/MiniGames/PropsLogic';
import { PropsModel, PropsSideEnum } from '../../../Models/MiniGames/PropsModel';
import { GameLogic } from '../../../Logic/GameLogic';
import { DialogTextEnum, DialogTypeTextEnum } from '../../../Models/GameModel';
import { PropsButtonController } from './PropsButtonController';
import { SoundConstants, SoundLevels } from '../../../Constants/SoundConstants';
import { PropsBarController } from './PropsBarController';

export interface IPropsGameControllerOptions {
    onComplete: () => void;
}

export class PropsGameController extends AnimationBaseController {
    public $model: PropsModel;

    private _width: number = 0;
    private _height: number = 0;

    private _backgroundShape: Graphics;
    private _animation: SpineAnimationBaseController;

    public constructor(
        @Inject private _options: IPropsGameControllerOptions,
        @Inject private _propsLogic: PropsLogic,
        @Inject private _gameLogic: GameLogic,
    ) {
        super('PropsGameController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return <IAnimationControllerConfiguration>{
            animationContent: AnimationContents.PROPS_GAME_SCREEN_JSON,
            libraryName: 'mini_game_props',
            sounds: [
                {
                    id: SoundConstants.ICEBREAKER,
                    type: SoundType.SFX,
                },
            ],
            childControllers: {
                animation: {
                    instanceName: "props",
                    controller: SpineAnimationBaseController,
                    model: this.$model,
                    options: {
                        key: `props`,
                        url: `/images/spine/mini_game_props/minigame_background.json`,
                        animations: [
                            'idle_1',
                            'idle_2',
                            'idle_3',
                            'idle_4',
                            'idle_5',
                            'start_1',
                            'start_2',
                            'start_3',
                            'start_4',
                            'start_5',
                            '1_go_left',
                            '2_go_left',
                            '3_go_left',
                            '4_go_left',
                            '5_go_left',
                            '1_go_straight',
                            '2_go_straight',
                            '3_go_straight',
                            '4_go_straight',
                            '5_go_straight',
                            '1_go_right',
                            '2_go_right',
                            '3_go_right',
                            '4_go_right',
                            '5_go_right',
                        ],
                    },
                },
                leftButton: {
                    instanceName: 'left',
                    controller: PropsButtonController,
                    options: {
                        onClick: () => this.onArrowClick(PropsSideEnum.left),
                    },
                },
                forwardButton: {
                    instanceName: 'forward',
                    controller: PropsButtonController,
                    options: {
                        onClick: () => this.onArrowClick(PropsSideEnum.forward),
                    },
                },
                rightButton: {
                    instanceName: 'right',
                    controller: PropsButtonController,
                    options: {
                        onClick: () => this.onArrowClick(PropsSideEnum.right),
                    },
                },
            },
            modelWatch: {
                step: (value: number) => {
                    if (value > 1) {
                        this.setToGo(value - 1);
                    } else {
                        this.setButtonsVisibility(false);
                        this.setToStart(value);
                    }
                },
            },
        };
    }

    protected onInitialize(): void {
        this._animation = this.$childControllers.get<SpineAnimationBaseController>('animation');

        this._width = 1920;
        this._height = 1200;

        this.display.setChildIndex(this._animation.display, 0);

        this._backgroundShape = new Graphics();
        this.display.addChildAt(this._backgroundShape, 0);

        this._propsLogic.start();
        this._gameLogic.showDialogScreen(DialogTextEnum.props, DialogTypeTextEnum.start);
        // this.getDisplayObjectByQuery("back")[0].scale.x = 2;
        this.addChildController({
            controller: PropsBarController,
            model: this.$model,
            options: {
            }
        }, 'progressbar');
    }

    protected moveArrows() {
        const right = this.getDisplayObjectByQuery('right')[0] as Sprite;
        const middle = this.getDisplayObjectByQuery('forward')[0] as Sprite;
        const left = this.getDisplayObjectByQuery('left')[0] as Sprite;
        right.y = 200;
        middle.y = 180;
        left.y = 200;
    }

    protected onResize(width: number, height: number, scale: number) {
        const scaleValue = Math.min(
            width / scale / this._width,
            height / scale / this._height,
        );
        this.y = height / scale * 0.15 / (width / height);
        // if ((height / scale) > (this.height * scaleValue)) {
        //     this.y += (height / scale - this._height);
        // }
        this.scaleX = scaleValue;
        this.scaleY = scaleValue;
        this.moveArrows();
        if (this._backgroundShape) {
            const w = width * 10;
            const h = height * 10;

            this._backgroundShape.clear();
            this._backgroundShape.beginFill(0x0074D6);
            this._backgroundShape.drawRect(-w, -h, w, h);
            this._backgroundShape.endFill();
        }
        //@ts-ignore
        this.$childControllers.get("animation").display.children[0].children[0].scale.x = 2 // sky
        //@ts-ignore
        this.$childControllers.get("animation").display.children[0].children[1].scale.x = 1.5
        //@ts-ignore
        this.$childControllers.get("animation").display.children[0].children[3].children[0].x = -3000;
        //@ts-ignore
        this.$childControllers.get("animation").display.children[0].children[2].children[0].x = 0
    }

    private setButtonsVisibility(value: boolean): void {
        this.$childControllers.get('leftButton').visible = value;
        this.$childControllers.get('forwardButton').visible = value;
        this.$childControllers.get('rightButton').visible = value;
    }

    private onArrowClick(side: PropsSideEnum): void {
        if (this._propsLogic.isRightSide(side)) {
            if (!this.soundManager.isSoundPlaying(SoundConstants.ICEBREAKER))
                this.soundManager.playSound(SoundConstants.ICEBREAKER, 0, SoundLevels.MINIGAME_AMBIENT);
            this._propsLogic.nextStep();
        } else {
            this._gameLogic.showDialogScreen(DialogTextEnum.props, DialogTypeTextEnum.hint);
        }
    }

    private setToGo(step: number): void {
        let side = this.$model.rightSides[step - 1];

        if (side === PropsSideEnum.forward) {
            side = 'straight';
        }

        this.setButtonsVisibility(false);

        this._animation.setAnimation(`${step}_go_${side}`, false);
        this._animation.getSpine().state.onComplete = () => {
            if (this._propsLogic.isComplete()) {
                this._animation.getSpine().state.onComplete = null;
                this._animation.resetAnimation();
                this._backgroundShape.visible = false;

                this._gameLogic.showDialogScreen(DialogTextEnum.props, DialogTypeTextEnum.end, 0, () => {
                    // TODO: spine runtime error without this dirty hack =/
                    setTimeout(() => {
                        this._options.onComplete();
                    }, 10);
                });
            } else {
                this.setToStart(step + 1);
            }
        };
    }

    private setToStart(step: number): void {
        this._animation.setAnimation(`start_${step}`, false);
        this._animation.getSpine().state.onComplete = () => {
            this._animation.getSpine().state.onComplete = null;
            this._animation.setAnimation(`idle_${step}`, true);

            this.setButtonsVisibility(true);
        };
    }
}
